import React, { useState, useEffect, useCallback } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import { DashCard, RankBars, DonutRing } from '../components/DashboardWidgets';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';
import { getBrandingSlug } from '../utils/formatters';

const UNITS       = ['kg','g','l','ml','piece','crate','bunch','tin','packet','dozen','tbsp','tsp'];
const CATEGORIES  = ['starter','main','dessert','salad','soup','snack','breakfast','beverage','other'];
const ING_CATS    = ['Proteins','Vegetables','Grains','Dairy','Fats & Oils','Spices','Beverages','Dry Goods','Other'];
const VAT_RATE       = 0.16;
const CATERING_LEVY  = 0.02;
const SERVICE_CHARGE = 0.10;
const blankIngredient = { name:'', qty:'', unit:'g', unit_cost:'', yield_pct:'100' };

const STATUS_META = {
  green:  { color:'#2ecc71', bg:'#0a1a0a', label:'✅ Under Target — Great margin!' },
  yellow: { color:'#f39c12', bg:'#1a1400', label:'⚠️ At Risk — Close to target' },
  red:    { color:'#e74c3c', bg:'#1a0a0a', label:'🔴 Over Target — Review pricing' },
  grey:   { color:'var(--textMuted)',    bg:'#111',    label:'— No selling price set' },
};
const MENU_ENG = {
  'Star':       { color:'#c8a96e', icon:'⭐', desc:'High margin + High popularity' },
  'Plow Horse': { color:'#3498db', icon:'🐎', desc:'Low margin + High popularity' },
  'Puzzle':     { color:'#9b59b6', icon:'🧩', desc:'High margin + Low popularity' },
  'Dog':        { color:'#e74c3c', icon:'🐕', desc:'Low margin + Low popularity' },
};

/* ─── Price Calculator ──────────────────────────────────────────────────────── */
function PriceCalculator({ notify }) {
  const [dishName,     setDishName]     = useState('');
  const [portions,     setPortions]     = useState('1');
  const [ingredients,  setIngredients]  = useState([{ ...blankIngredient }]);
  const [overhead,     setOverhead]     = useState('20');
  const [profitMargin, setProfitMargin] = useState('35');
  const [inclVAT,      setInclVAT]      = useState(true);
  const [inclLevy,     setInclLevy]     = useState(true);
  const [inclService,  setInclService]  = useState(false);
  const [roundTo,      setRoundTo]      = useState('5');
  const [menuType,     setMenuType]     = useState('food');
  const [menuCategory, setMenuCategory] = useState('Hot Drinks');
  const [publishing,   setPublishing]   = useState(false);
  const [published,    setPublished]    = useState(null);

  const updateIng = (i, field, val) =>
    setIngredients(prev => prev.map((ing, idx) => idx===i ? { ...ing, [field]:val } : ing));
  const addIng    = () => setIngredients(prev => [...prev, { ...blankIngredient }]);
  const removeIng = (i) => setIngredients(prev => prev.filter((_,idx) => idx!==i));

  const numPortions    = Math.max(1, parseFloat(portions)||1);
  const ingCosts       = ingredients.map(ing => {
    const qty=parseFloat(ing.qty)||0, unitCost=parseFloat(ing.unit_cost)||0;
    const yieldPct=Math.max(1,parseFloat(ing.yield_pct)||100)/100;
    const rawCost=qty*unitCost, trueCost=rawCost/yieldPct;
    return { ...ing, rawCost, trueCost };
  });
  const totalIngCost   = ingCosts.reduce((s,i)=>s+i.trueCost,0);
  const overheadAmt    = totalIngCost*(parseFloat(overhead)||0)/100;
  const totalFoodCost  = totalIngCost+overheadAmt;
  const foodCostPer    = totalFoodCost/numPortions;
  const gpFraction     = Math.min(0.99,(parseFloat(profitMargin)||0)/100);
  const priceBeforeTax = gpFraction>=1 ? foodCostPer*2 : foodCostPer/(1-gpFraction);
  const vatAmt         = inclVAT     ? priceBeforeTax*VAT_RATE       : 0;
  const levyAmt        = inclLevy    ? priceBeforeTax*CATERING_LEVY  : 0;
  const serviceAmt     = inclService ? priceBeforeTax*SERVICE_CHARGE : 0;
  const priceWithTax   = priceBeforeTax+vatAmt+levyAmt+serviceAmt;
  const rn             = parseFloat(roundTo)||1;
  const suggestedPrice = Math.ceil(priceWithTax/rn)*rn;
  const actualGP       = suggestedPrice>0 ? ((suggestedPrice-foodCostPer)/suggestedPrice)*100 : 0;
  const foodCostPct    = suggestedPrice>0 ? (foodCostPer/suggestedPrice)*100 : 0;

  const fmtK = n => 'KES '+n.toFixed(2);
  const inp  = { width:'100%',background:'#0e0e0e',border:'1px solid #2a2a2a',borderRadius:3,padding:'8px 10px',color:'#e0d0b0',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box' };
  const lbl  = { fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:4,display:'block' };
  const chk  = { accentColor:'#c8a96e',width:14,height:14 };

  return (
    <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12,alignItems:'start' }}>
      {/* LEFT */}
      <div>
        <Card title="🧮 Recipe & Ingredients">
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10,marginBottom:14 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label style={lbl}>Dish / Item Name</label>
              <input style={inp} placeholder="e.g. Cup of Tea" value={dishName} onChange={e=>setDishName(e.target.value)}/>
            </div>
            <div>
              <label style={lbl}>Number of Portions</label>
              <input style={inp} type="number" min="1" value={portions} onChange={e=>setPortions(e.target.value)}/>
            </div>
            <div>
              <label style={lbl}>Overhead / Labour %</label>
              <input style={inp} type="number" min="0" max="200" value={overhead} onChange={e=>setOverhead(e.target.value)}/>
              <div style={{ fontSize:10,color:'var(--textFaint)',marginTop:3 }}>% added on top of raw ingredient cost</div>
            </div>
          </div>
          <div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>Ingredients</div>
          <div style={{ display:'grid',gridTemplateColumns:'2fr 0.7fr 0.7fr 1fr 0.7fr 28px',gap:5,marginBottom:5,minWidth:480 }}>
            {['Ingredient','Qty','Unit','Cost/Unit (KES)','Yield %',''].map(h=>(
              <div key={h} style={{ fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {ingredients.map((ing,i)=>(
            <div key={i} style={{ display:'grid',gridTemplateColumns:'2fr 0.7fr 0.7fr 1fr 0.7fr 28px',gap:5,marginBottom:6,minWidth:480 }}>
              <input style={inp} placeholder="e.g. Milk" value={ing.name} onChange={e=>updateIng(i,'name',e.target.value)}/>
              <input style={inp} type="number" min="0" value={ing.qty} onChange={e=>updateIng(i,'qty',e.target.value)}/>
              <select style={inp} value={ing.unit} onChange={e=>updateIng(i,'unit',e.target.value)}>
                {['g','kg','ml','L','pcs','tbsp','tsp','cup'].map(u=><option key={u}>{u}</option>)}
              </select>
              <input style={inp} type="number" min="0" value={ing.unit_cost} onChange={e=>updateIng(i,'unit_cost',e.target.value)}/>
              <input style={inp} type="number" min="1" max="100" value={ing.yield_pct} onChange={e=>updateIng(i,'yield_pct',e.target.value)}/>
              <button onClick={()=>removeIng(i)} style={{ background:'#1a0a0a',border:'1px solid #3a1a1a',borderRadius:3,color:'#e74c3c',cursor:'pointer',fontSize:13 }}>✕</button>
            </div>
          ))}
          <button onClick={addIng} style={{ fontSize:11,padding:'6px 14px',background:'transparent',border:'1px solid #2a2a2a',borderRadius:3,color:'#c8a96e',cursor:'pointer',fontFamily:'inherit',marginTop:4 }}>+ Add Ingredient</button>
        </Card>

        <Card title="💰 Pricing & Tax Settings" style={{ marginTop:12 }}>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10,marginBottom:10 }}>
            <div>
              <label style={lbl}>Desired Profit Margin %</label>
              <input style={inp} type="number" min="0" max="99" value={profitMargin} onChange={e=>setProfitMargin(e.target.value)}/>
              <div style={{ fontSize:10,color:'var(--textFaint)',marginTop:3 }}>Gross profit as % of selling price</div>
            </div>
            <div>
              <label style={lbl}>Round Selling Price to Nearest</label>
              <select style={inp} value={roundTo} onChange={e=>setRoundTo(e.target.value)}>
                {['1','5','10','50','100'].map(v=><option key={v} value={v}>KES {v}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {[
              { label:'VAT (16%)',            state:inclVAT,     set:setInclVAT,     detail:'Kenya standard rate' },
              { label:'Catering Levy (2%)',   state:inclLevy,    set:setInclLevy,    detail:'Tourism Finance Corp' },
              { label:'Service Charge (10%)', state:inclService, set:setInclService, detail:'Optional — goes to staff' },
            ].map(({ label,state,set,detail })=>(
              <label key={label} style={{ display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'8px 10px',background:'#0e0e0e',border:'1px solid #1e1e1e',borderRadius:3 }}>
                <input type="checkbox" style={chk} checked={state} onChange={e=>set(e.target.checked)}/>
                <div>
                  <div style={{ fontSize:12,color:'#c0b090' }}>{label}</div>
                  <div style={{ fontSize:10,color:'var(--textFaint)' }}>{detail}</div>
                </div>
                <div style={{ marginLeft:'auto',fontSize:11,color:state?'#2ecc71':'#444' }}>{state?'Included':'Excluded'}</div>
              </label>
            ))}
          </div>
        </Card>
      </div>

      {/* RIGHT */}
      <div>
        <Card title={`📋 Cost Breakdown${dishName?' — '+dishName:''}`}>
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>
              Ingredient Costs (per batch of {numPortions} portion{numPortions!==1?'s':''})
            </div>
            {ingCosts.map((ing,i)=>{
              if (!ing.name && !ing.qty) return null;
              const yPct=Math.max(1,parseFloat(ing.yield_pct)||100), wastePct=100-yPct;
              return (
                <div key={i} style={{ display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #1a1a1a',fontSize:12 }}>
                  <div>
                    <span style={{ color:'#c0b090' }}>{ing.name||`Ingredient ${i+1}`}</span>
                    <span style={{ color:'var(--textFaint)',fontSize:10,marginLeft:8 }}>{ing.qty}{ing.unit} @ KES {parseFloat(ing.unit_cost||0).toFixed(2)}/{ing.unit}</span>
                    {wastePct>0 && <span style={{ color:'#e67e22',fontSize:10,marginLeft:8 }}>yield {yPct}% (+{wastePct}% waste)</span>}
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ color:'#c8a96e',fontWeight:600 }}>{fmtK(ing.trueCost)}</div>
                    {wastePct>0 && <div style={{ color:'var(--textFaint)',fontSize:10 }}>raw: {fmtK(ing.rawCost)}</div>}
                  </div>
                </div>
              );
            })}
            <div style={{ display:'flex',justifyContent:'space-between',padding:'8px 0',fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Total Ingredient Cost</span>
              <span style={{ color:'#c8a96e',fontWeight:700 }}>{fmtK(totalIngCost)}</span>
            </div>
            <div style={{ display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Overhead / Labour ({overhead}%)</span>
              <span style={{ color:'#c8a96e' }}>{fmtK(overheadAmt)}</span>
            </div>
            <div style={{ display:'flex',justifyContent:'space-between',padding:'6px 0',borderTop:'1px solid #2a2a2a',fontSize:13,fontWeight:700 }}>
              <span style={{ color:'var(--textMuted)' }}>Total Food Cost (batch)</span>
              <span style={{ color:'#e0d0b0' }}>{fmtK(totalFoodCost)}</span>
            </div>
            <div style={{ display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Food Cost per Portion</span>
              <span style={{ color:'#e0d0b0',fontWeight:600 }}>{fmtK(foodCostPer)}</span>
            </div>
          </div>

          <div style={{ background:'var(--bg)',border:'1px solid #1e1e1e',borderRadius:4,padding:14 }}>
            <div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:10 }}>Selling Price Build-Up (per portion)</div>
            {[
              { label:'Food Cost',                              val:foodCostPer,              color:'#e74c3c' },
              { label:`Gross Profit (${profitMargin}% margin)`,val:priceBeforeTax-foodCostPer,color:'#2ecc71' },
              { label:'Pre-tax Price',                          val:priceBeforeTax,            color:'#c8a96e',bold:true },
              inclVAT     && { label:'VAT (16%)',               val:vatAmt,                    color:'#e67e22' },
              inclLevy    && { label:'Catering Levy (2%)',      val:levyAmt,                   color:'#e67e22' },
              inclService && { label:'Service Charge (10%)',    val:serviceAmt,                color:'#e67e22' },
            ].filter(Boolean).map(({ label,val,color,bold })=>(
              <div key={label} style={{ display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #151515',fontSize:12 }}>
                <span style={{ color:'var(--textMuted)' }}>{label}</span>
                <span style={{ color,fontWeight:bold?700:400 }}>{fmtK(val)}</span>
              </div>
            ))}
            <div style={{ display:'flex',justifyContent:'space-between',padding:'6px 0',fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Price with taxes</span>
              <span style={{ color:'#c8a96e' }}>{fmtK(priceWithTax)}</span>
            </div>
          </div>

          <div style={{ background:'#0d1a0d',border:'1px solid #2ecc7133',borderRadius:4,padding:18,marginTop:12,textAlign:'center' }}>
            <div style={{ fontSize:10,color:'#2ecc71',letterSpacing:3,textTransform:'uppercase',marginBottom:6 }}>Suggested Selling Price</div>
            <div style={{ fontSize:42,fontWeight:700,color:'#2ecc71',letterSpacing:2 }}>KES {suggestedPrice.toLocaleString('en-KE')}</div>
            <div style={{ fontSize:11,color:'var(--textFaint)',marginTop:4 }}>Rounded to nearest KES {roundTo}</div>
          </div>

          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:8,marginTop:12 }}>
            {[
              { label:'Food Cost %',      val:`${foodCostPct.toFixed(1)}%`, color:foodCostPct>40?'#e74c3c':foodCostPct>30?'#e67e22':'#2ecc71' },
              { label:'Gross Profit %',   val:`${actualGP.toFixed(1)}%`,   color:actualGP<25?'#e74c3c':actualGP<35?'#e67e22':'#2ecc71' },
              { label:'Per Portion Cost', val:fmtK(foodCostPer),           color:'#c8a96e' },
            ].map(({ label,val,color })=>(
              <div key={label} style={{ background:'#0e0e0e',border:'1px solid #1e1e1e',borderRadius:4,padding:'10px 12px',textAlign:'center' }}>
                <div style={{ fontSize:9,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1,marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:18,fontWeight:700,color }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop:12,padding:'10px 14px',background:'var(--bgInput)',border:'1px solid var(--border)',borderRadius:4,fontSize:11,color:'var(--textMuted)',lineHeight:1.7 }}>
            <div style={{ color:'#c8a96e',fontWeight:600,marginBottom:4 }}>Industry Benchmarks</div>
            Food cost should ideally be <span style={{ color:'#2ecc71' }}>25–35%</span> of selling price. &nbsp;
            Gross profit margin of <span style={{ color:'#2ecc71' }}>65–75%</span> is healthy for a hotel restaurant. &nbsp;
            If food cost exceeds <span style={{ color:'#e74c3c' }}>40%</span>, review recipe yield, supplier pricing, or raise the selling price.
          </div>

          <div style={{ marginTop:12,background:'#080e08',border:'1px solid #2ecc7133',borderRadius:4,padding:16 }}>
            <div style={{ fontSize:11,color:'#2ecc71',letterSpacing:2,textTransform:'uppercase',marginBottom:10 }}>📋 Publish to Waiter Menu</div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:8,marginBottom:10 }}>
              <div>
                <label style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:4,display:'block' }}>Menu Section</label>
                <select style={{ width:'100%',background:'#0e0e0e',border:'1px solid #2a2a2a',borderRadius:3,padding:'8px 10px',color:'#e0d0b0',fontSize:13,fontFamily:'inherit' }}
                  value={menuType} onChange={e=>setMenuType(e.target.value)}>
                  <option value="food">🍽 Food Menu</option>
                  <option value="bar">🍺 Bar Menu</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:4,display:'block' }}>Category on Menu</label>
                <select style={{ width:'100%',background:'#0e0e0e',border:'1px solid #2a2a2a',borderRadius:3,padding:'8px 10px',color:'#e0d0b0',fontSize:13,fontFamily:'inherit' }}
                  value={menuCategory} onChange={e=>setMenuCategory(e.target.value)}>
                  {(menuType==='food'
                    ? ['Breakfast','Starters','Main Course','Salads','Soups','Snacks','Desserts','Hot Drinks','Other']
                    : ['Soft Drinks','Beers','Wines','Spirits','Cocktails','Hot Drinks','Water','Other']
                  ).map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ fontSize:11,color:'var(--textFaint)',marginBottom:10 }}>
              This will create <strong style={{ color:'#c0b090' }}>{dishName||'the dish'}</strong> on the <strong style={{ color:'#c0b090' }}>{menuType==='food'?'Food':'Bar'} menu</strong> at <strong style={{ color:'#2ecc71' }}>KES {suggestedPrice.toLocaleString('en-KE')}</strong>.
            </div>
            {published && (
              <div style={{ padding:'8px 12px',background:'#0a1a0a',border:'1px solid #2ecc7144',borderRadius:3,marginBottom:10,fontSize:12,color:'#2ecc71' }}>
                ✓ <strong>{published.name}</strong> is live on the menu at KES {published.price.toLocaleString('en-KE')}
              </div>
            )}
            <button
              disabled={!dishName||suggestedPrice<=0||publishing}
              style={{ width:'100%',background:(!dishName||suggestedPrice<=0)?'var(--bgInput)':'#1a3a1a',border:`1px solid ${(!dishName||suggestedPrice<=0)?'var(--borderMid)':'#2ecc7155'}`,borderRadius:3,padding:'11px',color:(!dishName||suggestedPrice<=0)?'#444':'#2ecc71',fontSize:13,fontWeight:700,cursor:(!dishName||suggestedPrice<=0)?'not-allowed':'pointer',fontFamily:'inherit',letterSpacing:1 }}
              onClick={async()=>{
                if (!dishName) { notify('Enter a dish name first','error'); return; }
                if (suggestedPrice<=0) { notify('Calculate a price first','error'); return; }
                setPublishing(true);
                try {
                  const res = await api.createMenuItem({ name:dishName,price:suggestedPrice,cost_price:foodCostPer,category:menuCategory,menu_type:menuType,description:`Food cost: KES ${foodCostPer.toFixed(2)} | GP: ${actualGP.toFixed(1)}%`,is_available:true });
                  setPublished({ name:res.data.name,price:suggestedPrice });
                  notify(`✓ "${dishName}" published to ${menuType==='food'?'Food':'Bar'} menu at KES ${suggestedPrice.toLocaleString('en-KE')}!`);
                } catch(e) { notify(e.response?.data?.name?.[0]||'Failed to publish to menu','error'); }
                finally { setPublishing(false); }
              }}>
              {publishing?'⏳ Publishing…':`📋 Add "${dishName||'Dish'}" to ${menuType==='food'?'Food':'Bar'} Menu — KES ${suggestedPrice.toLocaleString('en-KE')}`}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ─── Main ChefPage ──────────────────────────────────────────────────────────── */
/* ─── Spoilage Tab ──────────────────────────────────────────────────────────── */
const SPOIL_CATS = [
  { v:'expired',     l:'Expired / Past Use-By' },
  { v:'contaminated',l:'Contaminated / Pest Damage' },
  { v:'overcooked',  l:'Overcooked / Cooking Error' },
  { v:'returned',    l:'Customer Return' },
  { v:'damaged',     l:'Damaged in Storage' },
  { v:'leftover',    l:'Unsold Perishable Leftover' },
  { v:'other',       l:'Other' },
];
const SPOIL_UNITS = ['piece','plate','portion','kg','g','l','ml','tray','packet','bottle','other'];
const STATUS_COLORS = { draft:'#888', submitted:'#f39c12', approved:'#2ecc71', rejected:'#e74c3c' };

// ─── SPOILAGE CATEGORIES with hints ──────────────────────────────────────────
const SPOIL_CATS_EXT = [
  { v:'expired',      l:'Expired / Past Use-By',         icon:'⏰', hint:'Items past their use-by or best-before date' },
  { v:'contaminated', l:'Contaminated / Pest Damage',    icon:'🐀', hint:'Cross-contamination, pest activity or foreign objects' },
  { v:'overcooked',   l:'Overcooked / Cooking Error',    icon:'🔥', hint:'Cooking mistakes that made food unserviceable' },
  { v:'returned',     l:'Customer Return',               icon:'↩️', hint:'Food returned by guests and not reusable' },
  { v:'damaged',      l:'Damaged in Storage',            icon:'📦', hint:'Breakage, improper storage, temperature failure' },
  { v:'leftover',     l:'Unsold Perishable Leftover',    icon:'🍽️', hint:'End-of-service perishables that cannot be kept' },
  { v:'other',        l:'Other',                         icon:'❓', hint:'Any other cause not listed above' },
];

function SpoilageTab({ notify }) {
  const [reports, setReports]       = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState('list');
  const [editReport, setEditReport] = useState(null);
  const [form, setForm]             = useState({ title:'', category:'expired', date_occurred:'', description:'', preventive_action:'' });
  const [items, setItems]           = useState([{ item_name:'', quantity:'1', unit:'piece', unit_value:'', notes:'' }]);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving]         = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const [r, s] = await Promise.all([api.getSpoilageReports(params), api.getSpoilageSummary()]);
      setReports(r.data.results || r.data);
      setSummary(s.data);
    } catch(e) { notify('Failed to load spoilage data','error'); }
    setLoading(false);
  }, [filterStatus, notify]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditReport(null);
    const today = new Date().toISOString().split('T')[0];
    setForm({ title:'', category:'expired', date_occurred:today, description:'', preventive_action:'' });
    setItems([{ item_name:'', quantity:'1', unit:'piece', unit_value:'', notes:'' }]);
    setView('form');
  };

  const openEdit = (r) => {
    setEditReport(r);
    setForm({ title:r.title, category:r.category, date_occurred:r.date_occurred, description:r.description||'', preventive_action:r.preventive_action||'' });
    setItems(r.items.length > 0
      ? r.items.map(i=>({ item_name:i.item_name, quantity:String(i.quantity), unit:i.unit, unit_value:String(i.unit_value), notes:i.notes||'' }))
      : [{ item_name:'', quantity:'1', unit:'piece', unit_value:'', notes:'' }]);
    setView('form');
  };

  const cancelForm = () => { setView('list'); setEditReport(null); };
  const addItem    = () => setItems(p => [...p, { item_name:'', quantity:'1', unit:'piece', unit_value:'', notes:'' }]);
  const removeItem = i  => setItems(p => p.filter((_,idx)=>idx!==i));
  const updateItem = (i, field, val) => setItems(p => p.map((it,idx)=>idx===i?{...it,[field]:val}:it));
  const computedTotal = items.reduce((s,i) => s + (parseFloat(i.quantity)||0)*(parseFloat(i.unit_value)||0), 0);

  const save = async () => {
    if (!form.title || !form.date_occurred) { notify('Title and date required','error'); return; }
    if (items.some(i=>!i.item_name)) { notify('Every item needs a name','error'); return; }
    if (items.some(i=>!i.unit_value || parseFloat(i.unit_value)<=0)) { notify('Every item needs a unit value > 0','error'); return; }
    setSaving(true);
    try {
      let report;
      if (editReport) { const r = await api.updateSpoilageReport(editReport.id, form); report = r.data; }
      else            { const r = await api.createSpoilageReport(form);               report = r.data; }
      await api.setSpoilageItems(report.id, { items: items.map(i=>({ ...i, quantity:parseFloat(i.quantity)||1, unit_value:parseFloat(i.unit_value)||0 })) });
      notify('Report saved','success');
      setView('list');
      load();
    } catch(e) { notify('Save failed','error'); }
    setSaving(false);
  };

  const submit = async (id) => {
    try { await api.submitSpoilageReport(id); notify('Submitted for approval','success'); load(); }
    catch(e) { notify('Submit failed','error'); }
  };

  const approve = async (id) => {
    try { await api.approveSpoilageReport(id); notify('Approved — expense posted','success'); load(); }
    catch(e) { notify('Approval failed','error'); }
  };

  const doReject = async () => {
    if (!rejectReason) { notify('Please enter a reason','error'); return; }
    try { await api.rejectSpoilageReport(rejectModal, { reason: rejectReason }); notify('Report rejected','info'); setRejectModal(null); setRejectReason(''); load(); }
    catch(e) { notify('Reject failed','error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try { await api.deleteSpoilageReport(id); notify('Deleted','info'); load(); }
    catch(e) { notify('Delete failed','error'); }
  };

  const lbl = (text) => (
    <label style={{ display:'block', fontSize:11, color:'var(--textFaint)', marginBottom:5, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>{text}</label>
  );
  const tagStyle = (st) => ({ display:'inline-block', padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:700, color:STATUS_COLORS[st]||'#888', background:(STATUS_COLORS[st]||'#888')+'18', border:`1px solid ${STATUS_COLORS[st]||'#555'}44` });

  // ─── FORM VIEW ──────────────────────────────────────────────────────────────
  if (view === 'form') {
    const catObj = SPOIL_CATS_EXT.find(c=>c.v===form.category);
    return (
      <div style={{ maxWidth:800, margin:'0 auto' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
          <button onClick={cancelForm} style={{ background:'transparent', border:'1px solid #333', borderRadius:6, padding:'6px 14px', color:'var(--textMuted)', cursor:'pointer', fontSize:13 }}>← Back</button>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:'var(--textPrimary)' }}>
              {editReport ? '✏️ Edit Spoilage Report' : '🗑️ New Spoilage Report'}
            </div>
            <div style={{ fontSize:12, color:'var(--textMuted)' }}>
              {editReport ? `Editing ${editReport.ref}` : 'Document spoiled or wasted items for accountant review'}
            </div>
          </div>
        </div>

        {/* Section 1 — Details */}
        <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:10, padding:20, marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#c8a96e', textTransform:'uppercase', letterSpacing:1, marginBottom:16 }}>📋 Report Details</div>

          <div style={{ marginBottom:14 }}>
            {lbl('Report Title *')}
            <input style={{ ...inputStyle, margin:0, fontSize:14 }} value={form.title}
              onChange={e=>setForm(p=>({...p,title:e.target.value}))}
              placeholder="e.g. Morning Kitchen Waste — Fridge Failure" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              {lbl('Category *')}
              <select style={{ ...inputStyle, margin:0 }} value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                {SPOIL_CATS_EXT.map(c=><option key={c.v} value={c.v}>{c.icon} {c.l}</option>)}
              </select>
              {catObj && <div style={{ fontSize:11, color:'#888', marginTop:5, paddingLeft:2 }}>{catObj.hint}</div>}
            </div>
            <div>
              {lbl('Date of Occurrence *')}
              <input type="date" style={{ ...inputStyle, margin:0 }} value={form.date_occurred}
                onChange={e=>setForm(p=>({...p,date_occurred:e.target.value}))} />
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            {lbl('What Happened')}
            <textarea style={{ ...inputStyle, margin:0, minHeight:72, resize:'vertical' }} value={form.description}
              onChange={e=>setForm(p=>({...p,description:e.target.value}))}
              placeholder="Describe the cause — e.g. Fridge temperature rose overnight, produce in section B was spoiled by morning." />
          </div>

          <div>
            {lbl('Preventive Action')}
            <textarea style={{ ...inputStyle, margin:0, minHeight:58, resize:'vertical' }} value={form.preventive_action}
              onChange={e=>setForm(p=>({...p,preventive_action:e.target.value}))}
              placeholder="e.g. Set fridge alarm, improve FIFO rotation, retrain staff on storage temp checks." />
          </div>
        </div>

        {/* Section 2 — Items */}
        <div style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:10, padding:20, marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#c8a96e', textTransform:'uppercase', letterSpacing:1 }}>📦 Spoiled Items</div>
            <div style={{ fontSize:11, color:'var(--textFaint)' }}>{items.length} item{items.length!==1?'s':''}</div>
          </div>

          {/* Column labels */}
          <div style={{ display:'grid', gridTemplateColumns:'2.5fr 76px 88px 108px 1.5fr 36px', gap:8, marginBottom:6, paddingBottom:6, borderBottom:'1px solid #1a1a1a' }}>
            {['Item Name','Qty','Unit','Unit Value (KES)','Notes',''].map((h,i)=>(
              <div key={i} style={{ fontSize:10, color:'var(--textFaint)', fontWeight:700, textTransform:'uppercase', letterSpacing:0.4 }}>{h}</div>
            ))}
          </div>

          {items.map((it,i)=>{
            const lineTotal = (parseFloat(it.quantity)||0)*(parseFloat(it.unit_value)||0);
            return (
              <div key={i}>
                <div style={{ display:'grid', gridTemplateColumns:'2.5fr 76px 88px 108px 1.5fr 36px', gap:8, marginBottom:4, alignItems:'center' }}>
                  <input style={{ ...inputStyle, margin:0 }} value={it.item_name}
                    onChange={e=>updateItem(i,'item_name',e.target.value)} placeholder="e.g. Chicken breast" />
                  <input type="number" min="0" step="0.1" style={{ ...inputStyle, margin:0, textAlign:'right' }}
                    value={it.quantity} onChange={e=>updateItem(i,'quantity',e.target.value)} />
                  <select style={{ ...inputStyle, margin:0 }} value={it.unit} onChange={e=>updateItem(i,'unit',e.target.value)}>
                    {SPOIL_UNITS.map(u=><option key={u}>{u}</option>)}
                  </select>
                  <input type="number" min="0" style={{ ...inputStyle, margin:0, textAlign:'right' }}
                    value={it.unit_value} onChange={e=>updateItem(i,'unit_value',e.target.value)} placeholder="0" />
                  <input style={{ ...inputStyle, margin:0 }} value={it.notes}
                    onChange={e=>updateItem(i,'notes',e.target.value)} placeholder="Optional" />
                  <button onClick={()=>removeItem(i)} disabled={items.length===1}
                    style={{ width:34, height:34, border:'1px solid #3a1010', borderRadius:6, background:'#1a0808',
                      color:items.length===1?'#333':'#e74c3c', cursor:items.length===1?'default':'pointer',
                      fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
                </div>
                {lineTotal > 0 && (
                  <div style={{ textAlign:'right', fontSize:11, color:'#e74c3c', marginBottom:8, paddingRight:44 }}>
                    Line total: KES {lineTotal.toLocaleString(undefined,{minimumFractionDigits:2})}
                  </div>
                )}
              </div>
            );
          })}

          <button onClick={addItem}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', border:'1px dashed #333',
              borderRadius:6, background:'transparent', color:'var(--textMuted)', cursor:'pointer', fontSize:13,
              marginTop:4, width:'100%', justifyContent:'center' }}>
            + Add Another Item
          </button>
        </div>

        {/* Total banner */}
        <div style={{ background:computedTotal>0?'#1a0808':'#111', border:`1px solid ${computedTotal>0?'#e74c3c44':'#222'}`,
          borderRadius:10, padding:'14px 20px', marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:2 }}>Total Spoilage Value</div>
            <div style={{ fontSize:11, color:'var(--textFaint)' }}>
              {items.filter(i=>i.item_name).length} named item{items.filter(i=>i.item_name).length!==1?'s':''}
            </div>
          </div>
          <div style={{ fontSize:26, fontWeight:900, color:computedTotal>0?'#e74c3c':'#333' }}>
            KES {computedTotal.toLocaleString(undefined,{minimumFractionDigits:2})}
          </div>
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button style={{ ...btnSm, padding:'10px 20px' }} onClick={cancelForm}>Cancel</button>
          <button style={{ ...btnPrimary, padding:'10px 28px', fontSize:14 }} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : editReport ? '💾 Update Report' : '💾 Save as Draft'}
          </button>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────
  return (
    <div>
      {summary && (
        <StatCards stats={[
          { label:'This Month (Approved)', value:`KES ${(summary.this_month||0).toLocaleString()}`, color:'#e74c3c', icon:'🗑️' },
          { label:'This Year (Approved)',  value:`KES ${(summary.this_year||0).toLocaleString()}`,  color:'#e67e22', icon:'📅' },
          { label:'Pending Approval',      value:summary.pending_count||0,                          color:'#f39c12', icon:'⏳' },
        ]} />
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {['','draft','submitted','approved','rejected'].map(s=>(
            <button key={s} style={{ ...btnSm, background:filterStatus===s?'#c8a96e':'transparent', color:filterStatus===s?'#000':'var(--textMuted)', border:'1px solid #333' }}
              onClick={()=>setFilterStatus(s)}>{s ? s.charAt(0).toUpperCase()+s.slice(1) : 'All'}</button>
          ))}
        </div>
        <button style={btnPrimary} onClick={openNew}>+ New Spoilage Report</button>
      </div>

      {loading ? <div style={{ color:'var(--textMuted)', padding:24, textAlign:'center' }}>Loading…</div>
       : reports.length===0 ? <Empty text="No spoilage reports yet" />
       : reports.map(r => {
        const isOpen = expandedId === r.id;
        return (
          <div key={r.id} style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderLeft:`3px solid ${STATUS_COLORS[r.status]||'#333'}`, borderRadius:8, marginBottom:10, overflow:'hidden' }}>
            {/* Collapsed header */}
            <div onClick={()=>setExpandedId(isOpen?null:r.id)}
              style={{ padding:'12px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
                  <span style={{ fontWeight:700, color:'#c8a96e', fontSize:13 }}>{r.ref}</span>
                  <span style={tagStyle(r.status)}>{r.status_label||r.status}</span>
                  <span style={{ fontSize:11, color:'var(--textFaint)' }}>{r.date_occurred}</span>
                  <span style={{ fontSize:11, color:'var(--textFaint)' }}>· {r.category_label||r.category}</span>
                </div>
                <div style={{ fontWeight:600, fontSize:14, color:'var(--textPrimary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r.title}</div>
                <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>
                  {r.items?.length||0} item{r.items?.length!==1?'s':''} · by {r.reported_by_name||'—'}
                  {r.approved_by_name ? ` · approved by ${r.approved_by_name}` : ''}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:14, flexShrink:0 }}>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:20, fontWeight:900, color:r.status==='approved'?'#e74c3c':'#c8a96e' }}>
                    KES {parseFloat(r.total_value||0).toLocaleString(undefined,{minimumFractionDigits:2})}
                  </div>
                  <div style={{ fontSize:10, color:'var(--textFaint)' }}>total waste value</div>
                </div>
                <div style={{ color:'var(--textFaint)', fontSize:16 }}>{isOpen?'▲':'▼'}</div>
              </div>
            </div>

            {/* Expanded body */}
            {isOpen && (
              <div style={{ borderTop:'1px solid #1a1a1a', padding:'14px 16px' }}>
                {r.description && (
                  <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:12, lineHeight:1.6, padding:'8px 12px', background:'#0d0d0d', borderRadius:6, borderLeft:'3px solid #333' }}>
                    {r.description}
                  </div>
                )}
                {r.items && r.items.length > 0 && (
                  <div style={{ overflowX:'auto', marginBottom:12 }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead>
                        <tr style={{ background:'#0d0d0d' }}>
                          {['Item','Qty','Unit','Unit Value','Total Value','Notes'].map(h=>(
                            <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:'#c8a96e', fontWeight:600, fontSize:11 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {r.items.map((it,idx)=>(
                          <tr key={idx} style={{ borderBottom:'1px solid #1a1a1a' }}>
                            <td style={{ padding:'6px 10px', fontWeight:600 }}>{it.item_name}</td>
                            <td style={{ padding:'6px 10px' }}>{it.quantity}</td>
                            <td style={{ padding:'6px 10px', color:'var(--textMuted)' }}>{it.unit}</td>
                            <td style={{ padding:'6px 10px' }}>KES {parseFloat(it.unit_value).toLocaleString()}</td>
                            <td style={{ padding:'6px 10px', fontWeight:700, color:'#e74c3c' }}>KES {parseFloat(it.total_value||0).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                            <td style={{ padding:'6px 10px', color:'var(--textFaint)' }}>{it.notes||'—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {r.preventive_action && (
                  <div style={{ fontSize:12, background:'#0a1a0a', padding:'8px 12px', borderRadius:4, borderLeft:'3px solid #2ecc71', marginBottom:12 }}>
                    <span style={{ fontWeight:700, color:'#2ecc71' }}>Preventive Action: </span>
                    <span style={{ color:'var(--textMuted)' }}>{r.preventive_action}</span>
                  </div>
                )}
                {r.status==='approved' && <div style={{ fontSize:12, color:'#2ecc71', marginBottom:10 }}>✅ Approved by {r.approved_by_name||'—'} — posted to Food & Beverage expenses</div>}
                {r.status==='rejected' && <div style={{ fontSize:12, color:'#e74c3c', marginBottom:10 }}>❌ Rejected: {r.rejection_reason||'No reason given'}</div>}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {r.status==='draft' && <>
                    <button style={{ ...btnSm, background:'#1a2a1a' }} onClick={()=>openEdit(r)}>✏️ Edit</button>
                    <button style={{ ...btnSm, background:'#1a3a2a' }} onClick={()=>submit(r.id)}>📤 Submit for Approval</button>
                    <button style={{ ...btnSm, background:'#2a1010' }} onClick={()=>del(r.id)}>🗑 Delete</button>
                  </>}
                  {r.status==='submitted' && <>
                    <button style={{ ...btnSm, background:'#1a6b3a' }} onClick={()=>approve(r.id)}>✅ Approve — Post KES {parseFloat(r.total_value).toLocaleString()} to Expenses</button>
                    <button style={{ ...btnSm, background:'#5a1010' }} onClick={()=>{ setRejectModal(r.id); setRejectReason(''); }}>❌ Reject</button>
                  </>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {rejectModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
          <div style={{ background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:8,padding:24,maxWidth:400,width:'100%' }}>
            <div style={{ fontWeight:700,color:'#e74c3c',marginBottom:12,fontSize:15 }}>❌ Reject Spoilage Report</div>
            <Field label="Reason for rejection">
              <textarea style={{ ...inputStyle,height:80 }} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="Explain why this is being rejected…" />
            </Field>
            <div style={{ display:'flex',gap:10,marginTop:12 }}>
              <button style={{ ...btnPrimary, background:'#8B2635' }} onClick={doReject}>Reject</button>
              <button style={btnSm} onClick={()=>setRejectModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Budget & Staff Meals Tab ──────────────────────────────────────────────────
const MONTH_NAMES = ['','January','February','March','April','May','June','July','August','September','October','November','December'];
const MEAL_TYPES  = [
  { id:'breakfast', label:'🌅 Breakfast' },
  { id:'lunch',     label:'🍽️ Lunch'    },
  { id:'dinner',    label:'🌙 Dinner'   },
  { id:'snack',     label:'☕ Tea Break' },
];

function BudgetTab({ notify }) {
  const now   = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  // Budget state
  const [summary,       setSummary]       = useState(null);
  const [budgetForm,    setBudgetForm]    = useState({ ingredients_budget:'', staff_meals_budget:'', overhead_budget:'', notes:'' });
  const [editingBudget, setEditingBudget] = useState(false);
  const [savingBudget,  setSavingBudget]  = useState(false);

  // Staff meal state
  const [meals,       setMeals]       = useState([]);
  const [mealForm,    setMealForm]    = useState({ date: now.toISOString().slice(0,10), meal_type:'lunch', description:'', num_staff:'', cost_per_head:'', notes:'' });
  const [editMeal,    setEditMeal]    = useState(null);
  const [mealModal,   setMealModal]   = useState(false);
  const [savingMeal,  setSavingMeal]  = useState(false);
  const [deleting,    setDeleting]    = useState(null);

  const inp = { width:'100%', background:'#0e0e0e', border:'1px solid #2a2a2a', borderRadius:4, padding:'8px 10px', color:'#e0d0b0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box' };
  const lbl = { fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:4, display:'block' };
  const fmtK = v => 'KES ' + Number(v||0).toLocaleString('en-KE', { minimumFractionDigits:2, maximumFractionDigits:2 });

  const loadAll = useCallback(async () => {
    try {
      const [sumRes, mealsRes] = await Promise.all([
        api.getChefBudgetSummary(`?year=${year}&month=${month}`),
        api.getStaffMealLogs(`?year=${year}&month=${month}`),
      ]);
      const s = sumRes.data;
      setSummary(s);
      if (s.budget) {
        setBudgetForm({
          ingredients_budget: s.budget.ingredients_budget,
          staff_meals_budget: s.budget.staff_meals_budget,
          overhead_budget:    s.budget.overhead_budget,
          notes:              s.budget.notes || '',
        });
      } else {
        setBudgetForm({ ingredients_budget:'', staff_meals_budget:'', overhead_budget:'', notes:'' });
      }
      setMeals(mealsRes.data.results || mealsRes.data);
    } catch(e) { notify('Failed to load budget data','error'); }
  }, [year, month, notify]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Budget save ──────────────────────────────────────────────────────────────
  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const payload = {
        year, month,
        ingredients_budget: parseFloat(budgetForm.ingredients_budget) || 0,
        staff_meals_budget:  parseFloat(budgetForm.staff_meals_budget)  || 0,
        overhead_budget:     parseFloat(budgetForm.overhead_budget)     || 0,
        notes: budgetForm.notes,
      };
      if (summary?.budget?.id) {
        await api.updateChefBudget(summary.budget.id, payload);
      } else {
        await api.createChefBudget(payload);
      }
      setEditingBudget(false);
      await loadAll();
      notify('Budget saved ✓');
    } catch(e) {
      const err = e.response?.data;
      const msg = err?.non_field_errors?.[0] || err?.detail || 'Error saving budget';
      notify(msg, 'error');
    }
    setSavingBudget(false);
  };

  // ── Meal log save ────────────────────────────────────────────────────────────
  const openMealModal = (meal=null) => {
    if (meal) {
      setEditMeal(meal);
      setMealForm({ date:meal.date, meal_type:meal.meal_type, description:meal.description||'', num_staff:meal.num_staff, cost_per_head:meal.cost_per_head, notes:meal.notes||'' });
    } else {
      setEditMeal(null);
      setMealForm({ date: new Date().toISOString().slice(0,10), meal_type:'lunch', description:'', num_staff:'', cost_per_head:'', notes:'' });
    }
    setMealModal(true);
  };

  const saveMeal = async () => {
    if (!mealForm.num_staff || !mealForm.cost_per_head) { notify('Number of staff and cost per head are required','error'); return; }
    setSavingMeal(true);
    try {
      const payload = {
        date:          mealForm.date,
        meal_type:     mealForm.meal_type,
        description:   mealForm.description,
        num_staff:     parseInt(mealForm.num_staff) || 0,
        cost_per_head: parseFloat(mealForm.cost_per_head) || 0,
        notes:         mealForm.notes,
      };
      if (editMeal) await api.updateStaffMealLog(editMeal.id, payload);
      else          await api.createStaffMealLog(payload);
      setMealModal(false);
      await loadAll();
      notify(editMeal ? 'Meal log updated ✓' : 'Meal logged ✓');
    } catch(e) { notify('Error saving meal log','error'); }
    setSavingMeal(false);
  };

  const deleteMeal = async (id) => {
    setDeleting(id);
    try { await api.deleteStaffMealLog(id); await loadAll(); notify('Entry deleted'); }
    catch { notify('Delete failed','error'); }
    setDeleting(null);
  };

  // ── Derived numbers ──────────────────────────────────────────────────────────
  const budgIng   = parseFloat(summary?.budget?.ingredients_budget  || 0);
  const budgMeals = parseFloat(summary?.budget?.staff_meals_budget   || 0);
  const budgOH    = parseFloat(summary?.budget?.overhead_budget      || 0);
  const budgTotal = budgIng + budgMeals + budgOH;

  const actMeals  = parseFloat(summary?.actual?.staff_meals    || 0);
  const actIng    = parseFloat(summary?.actual?.ingredients     || 0);
  const actTotal  = actMeals + actIng;

  const mealTotal = meals.reduce((s,m) => s + parseFloat(m.total_cost||0), 0);
  const mealStaff = meals.reduce((s,m) => s + parseInt(m.num_staff||0), 0);

  const pct = (actual, budget) => budget > 0 ? Math.min(100, (actual/budget)*100) : 0;
  const barColor = (actual, budget) => {
    const p = pct(actual, budget);
    if (p > 95) return '#e74c3c';
    if (p > 80) return '#f39c12';
    return '#2ecc71';
  };

  const mealFormTotal = (parseFloat(mealForm.num_staff)||0) * (parseFloat(mealForm.cost_per_head)||0);

  const prevMonth = () => { if (month===1) { setYear(y=>y-1); setMonth(12); } else setMonth(m=>m-1); };
  const nextMonth = () => {
    const n = new Date(); if (year > n.getFullYear() || (year === n.getFullYear() && month >= n.getMonth()+1)) return;
    if (month===12) { setYear(y=>y+1); setMonth(1); } else setMonth(m=>m+1);
  };
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth()+1;

  // Group meals by date for display
  const mealsByDate = meals.reduce((acc, m) => {
    const d = m.date; if (!acc[d]) acc[d] = []; acc[d].push(m); return acc;
  }, {});
  const sortedDates = Object.keys(mealsByDate).sort((a,b) => b.localeCompare(a));

  const cardStyle = { background:'#0d0d0d', border:'1px solid #1e1e1e', borderRadius:8, padding:16, marginBottom:12 };
  const metaLbl  = { fontSize:9, color:'var(--textFaint)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:2 };

  return (
    <div style={{ padding:'4px 0' }}>

      {/* ── Month Navigator ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <button onClick={prevMonth} style={{ background:'#111', border:'1px solid #2a2a2a', borderRadius:4, color:'#c8a96e', cursor:'pointer', padding:'6px 14px', fontSize:14 }}>‹</button>
        <div style={{ flex:1, textAlign:'center' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e' }}>{MONTH_NAMES[month]} {year}</div>
          <div style={{ fontSize:10, color:'var(--textFaint)' }}>Kitchen Budget &amp; Staff Meals</div>
        </div>
        <button onClick={nextMonth} disabled={isCurrentMonth} style={{ background:'#111', border:'1px solid #2a2a2a', borderRadius:4, color: isCurrentMonth ? '#333' : '#c8a96e', cursor: isCurrentMonth ? 'default':'pointer', padding:'6px 14px', fontSize:14 }}>›</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:14, alignItems:'start' }}>

        {/* ══ LEFT: Budget ══════════════════════════════════════════════════════ */}
        <div>
          {/* Budget summary card */}
          <div style={cardStyle}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#c8a96e', letterSpacing:1 }}>💰 MONTHLY BUDGET</div>
              <button
                onClick={() => setEditingBudget(e => !e)}
                style={{ fontSize:10, padding:'4px 12px', background: editingBudget?'rgba(200,169,110,0.15)':'transparent', border:'1px solid #2a2a2a', borderRadius:4, color:'#c8a96e', cursor:'pointer', fontFamily:'inherit' }}
              >
                {editingBudget ? '✕ Cancel' : summary?.budget ? '✎ Edit' : '+ Set Budget'}
              </button>
            </div>

            {editingBudget ? (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                  <div>
                    <label style={lbl}>🛒 Ingredients (KES)</label>
                    <input style={inp} type="number" min="0" placeholder="e.g. 150000"
                      value={budgetForm.ingredients_budget}
                      onChange={e=>setBudgetForm(f=>({...f,ingredients_budget:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={lbl}>🍽️ Staff Meals (KES)</label>
                    <input style={inp} type="number" min="0" placeholder="e.g. 30000"
                      value={budgetForm.staff_meals_budget}
                      onChange={e=>setBudgetForm(f=>({...f,staff_meals_budget:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={lbl}>⚙️ Overhead / Gas (KES)</label>
                    <input style={inp} type="number" min="0" placeholder="e.g. 20000"
                      value={budgetForm.overhead_budget}
                      onChange={e=>setBudgetForm(f=>({...f,overhead_budget:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={lbl}>Total (auto)</label>
                    <div style={{ ...inp, color:'#c8a96e', fontWeight:700, background:'#070707' }}>
                      {fmtK((parseFloat(budgetForm.ingredients_budget)||0)+(parseFloat(budgetForm.staff_meals_budget)||0)+(parseFloat(budgetForm.overhead_budget)||0))}
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={lbl}>Notes</label>
                  <textarea style={{ ...inp, height:60, resize:'vertical' }}
                    placeholder="Optional notes for this month's budget..."
                    value={budgetForm.notes}
                    onChange={e=>setBudgetForm(f=>({...f,notes:e.target.value}))}/>
                </div>
                <button
                  onClick={saveBudget} disabled={savingBudget}
                  style={{ width:'100%', padding:'9px', background:'#c8a96e', border:'none', borderRadius:4, color:'#000', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', opacity: savingBudget?0.6:1 }}
                >
                  {savingBudget ? 'Saving…' : `💾 Save ${MONTH_NAMES[month]} Budget`}
                </button>
              </div>
            ) : summary?.budget ? (
              <div>
                {/* Visual budget bars */}
                {[
                  { label:'🛒 Ingredients', budget: budgIng,   actual: actIng,   note:'vs purchases' },
                  { label:'🍽️ Staff Meals', budget: budgMeals, actual: actMeals, note:'vs meal logs'  },
                  { label:'⚙️ Overhead',    budget: budgOH,    actual: 0,        note:'tracked manually' },
                ].map(row => {
                  const p = pct(row.actual, row.budget);
                  const bc = barColor(row.actual, row.budget);
                  return (
                    <div key={row.label} style={{ marginBottom:14 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:12, color:'#ddd' }}>{row.label}</span>
                        <span style={{ fontSize:11, color: bc, fontWeight:700 }}>
                          {fmtK(row.actual)} / {fmtK(row.budget)}
                        </span>
                      </div>
                      <div style={{ height:6, background:'#1a1a1a', borderRadius:3, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${p}%`, background: bc, borderRadius:3, transition:'width 0.5s' }}/>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', marginTop:2 }}>
                        <span style={{ fontSize:9, color:'#444' }}>{row.note}</span>
                        <span style={{ fontSize:9, color: p>95?'#e74c3c':p>80?'#f39c12':'#555' }}>
                          {p.toFixed(0)}% used{row.budget>0 && row.actual<row.budget ? ` · ${fmtK(row.budget-row.actual)} remaining` : p>100?' · OVER BUDGET':''}
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div style={{ borderTop:'1px solid #1c1c1c', paddingTop:10, marginTop:4 }}>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:12, color:'var(--textMuted)' }}>Total Budget</span>
                    <span style={{ fontSize:13, fontWeight:700, color:'#c8a96e' }}>{fmtK(budgTotal)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                    <span style={{ fontSize:12, color:'var(--textMuted)' }}>Total Actual Spend</span>
                    <span style={{ fontSize:13, fontWeight:700, color: actTotal>budgTotal?'#e74c3c':'#2ecc71' }}>{fmtK(actTotal)}</span>
                  </div>
                  {budgTotal > 0 && (
                    <div style={{ fontSize:10, color:'#555', textAlign:'right', marginTop:2 }}>
                      {actTotal<=budgTotal ? `${fmtK(budgTotal-actTotal)} under budget` : `${fmtK(actTotal-budgTotal)} OVER budget`}
                    </div>
                  )}
                </div>

                {summary.budget.notes && (
                  <div style={{ marginTop:10, padding:'8px 10px', background:'#0a0a0a', borderRadius:4, fontSize:11, color:'#555', fontStyle:'italic' }}>
                    {summary.budget.notes}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign:'center', padding:'30px 16px', color:'#2e2e2e' }}>
                <div style={{ fontSize:28, marginBottom:8 }}>💰</div>
                <div style={{ fontSize:13, marginBottom:4, color:'#444' }}>No budget set for {MONTH_NAMES[month]} {year}</div>
                <div style={{ fontSize:11, color:'#2e2e2e' }}>Click "+ Set Budget" to define this month's kitchen targets</div>
              </div>
            )}
          </div>

          {/* ── Meal summary stats ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            {[
              { label:'Total Meal Cost', value: fmtK(mealTotal),   color:'#c8a96e', icon:'💰' },
              { label:'Staff Served',    value: mealStaff.toLocaleString(), color:'#3498db', icon:'👥' },
              { label:'Meal Sessions',   value: meals.length,       color:'#9b59b6', icon:'🍽️' },
              { label:'Avg Cost/Session',value: meals.length > 0 ? fmtK(mealTotal/meals.length) : '—', color:'#2ecc71', icon:'📊' },
            ].map(s => (
              <div key={s.label} style={{ ...cardStyle, marginBottom:0, padding:'12px 14px' }}>
                <div style={{ fontSize:16, marginBottom:4 }}>{s.icon}</div>
                <div style={{ fontSize:16, fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Breakdown by meal type ── */}
          {meals.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontSize:11, fontWeight:700, color:'#888', letterSpacing:1, marginBottom:12 }}>BREAKDOWN BY MEAL TYPE</div>
              {MEAL_TYPES.map(mt => {
                const group = meals.filter(m=>m.meal_type===mt.id);
                if (!group.length) return null;
                const cost  = group.reduce((s,m)=>s+parseFloat(m.total_cost||0),0);
                const staff = group.reduce((s,m)=>s+parseInt(m.num_staff||0),0);
                return (
                  <div key={mt.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #161616' }}>
                    <div>
                      <div style={{ fontSize:12, color:'#ccc' }}>{mt.label}</div>
                      <div style={{ fontSize:10, color:'#444' }}>{group.length} sessions · {staff} staff</div>
                    </div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#c8a96e' }}>{fmtK(cost)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ══ RIGHT: Staff Meal Log ════════════════════════════════════════════ */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#c8a96e', letterSpacing:1 }}>🍽️ STAFF MEAL LOG</div>
            <button
              onClick={() => openMealModal()}
              style={{ fontSize:11, padding:'6px 14px', background:'rgba(200,169,110,0.12)', border:'1px solid #c8a96e', borderRadius:4, color:'#c8a96e', cursor:'pointer', fontFamily:'inherit', fontWeight:700 }}
            >
              + Log Meal
            </button>
          </div>

          {meals.length === 0 ? (
            <div style={{ ...cardStyle, textAlign:'center', padding:'40px 16px', color:'#2e2e2e' }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🍽️</div>
              <div style={{ fontSize:13, color:'#444' }}>No staff meals logged for {MONTH_NAMES[month]}</div>
              <div style={{ fontSize:11, color:'#2e2e2e', marginTop:4 }}>Click "+ Log Meal" to record today's staff meal</div>
            </div>
          ) : (
            sortedDates.map(date => {
              const dayMeals    = mealsByDate[date];
              const dayTotal    = dayMeals.reduce((s,m) => s + parseFloat(m.total_cost||0), 0);
              const dayStaff    = dayMeals.reduce((s,m) => s + parseInt(m.num_staff||0), 0);
              const dateObj     = new Date(date + 'T12:00:00');
              const dateLabel   = dateObj.toLocaleDateString('en-KE', { weekday:'short', day:'2-digit', month:'short' });
              return (
                <div key={date} style={{ ...cardStyle }}>
                  {/* Day header */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#ddd' }}>{dateLabel}</div>
                      <div style={{ fontSize:10, color:'#444' }}>{dayMeals.length} session{dayMeals.length!==1?'s':''} · {dayStaff} staff</div>
                    </div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#c8a96e' }}>{fmtK(dayTotal)}</div>
                  </div>

                  {/* Meal entries for this day */}
                  {dayMeals.map(m => {
                    const mt = MEAL_TYPES.find(x=>x.id===m.meal_type);
                    return (
                      <div key={m.id} style={{
                        display:'flex', gap:10, alignItems:'flex-start',
                        padding:'9px 10px', background:'#070707',
                        border:'1px solid #181818', borderRadius:5, marginBottom:7,
                      }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:3, flexWrap:'wrap' }}>
                            <span style={{ fontSize:10, background:'rgba(200,169,110,0.1)', border:'1px solid #2a2a2a', borderRadius:8, padding:'1px 8px', color:'#c8a96e' }}>
                              {mt?.label || m.meal_type}
                            </span>
                            <span style={{ fontSize:11, color:'#e74c3c', fontWeight:700 }}>
                              {fmtK(m.total_cost)}
                            </span>
                          </div>
                          {m.description && (
                            <div style={{ fontSize:12, color:'#888', marginBottom:3 }}>{m.description}</div>
                          )}
                          <div style={{ fontSize:10, color:'#444' }}>
                            {m.num_staff} staff × {fmtK(m.cost_per_head)}/head
                            {m.logged_by_name && ` · Logged by ${m.logged_by_name}`}
                          </div>
                          {m.notes && <div style={{ fontSize:10, color:'#3a3a3a', marginTop:2, fontStyle:'italic' }}>{m.notes}</div>}
                        </div>
                        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                          <button
                            onClick={() => openMealModal(m)}
                            style={{ fontSize:11, padding:'4px 10px', background:'transparent', border:'1px solid #2a2a2a', borderRadius:3, color:'#888', cursor:'pointer' }}
                          >✎</button>
                          <button
                            onClick={() => { if (window.confirm('Delete this meal log entry?')) deleteMeal(m.id); }}
                            disabled={deleting===m.id}
                            style={{ fontSize:11, padding:'4px 8px', background:'#1a0808', border:'1px solid #3a1515', borderRadius:3, color:'#e74c3c', cursor:'pointer', opacity: deleting===m.id?0.5:1 }}
                          >{deleting===m.id?'…':'✕'}</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Log Meal Modal ── */}
      {mealModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#0d0d0d', border:'1px solid #2a2a2a', borderRadius:8, padding:24, maxWidth:440, width:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e', marginBottom:4 }}>
              {editMeal ? '✎ Edit Meal Log' : '🍽️ Log Staff Meal'}
            </div>
            <div style={{ fontSize:11, color:'var(--textFaint)', marginBottom:18 }}>
              Record a staff meal as a kitchen expense
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <div>
                <label style={lbl}>Date</label>
                <input style={inp} type="date" value={mealForm.date}
                  onChange={e=>setMealForm(f=>({...f,date:e.target.value}))}/>
              </div>
              <div>
                <label style={lbl}>Meal Type</label>
                <select style={inp} value={mealForm.meal_type}
                  onChange={e=>setMealForm(f=>({...f,meal_type:e.target.value}))}>
                  {MEAL_TYPES.map(mt=><option key={mt.id} value={mt.id}>{mt.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:12 }}>
              <label style={lbl}>Menu Description</label>
              <input style={inp} placeholder="e.g. Rice & beans, Ugali & sukuma wiki, Chicken stew..."
                value={mealForm.description}
                onChange={e=>setMealForm(f=>({...f,description:e.target.value}))}/>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <div>
                <label style={lbl}>Number of Staff</label>
                <input style={inp} type="number" min="1" placeholder="e.g. 45"
                  value={mealForm.num_staff}
                  onChange={e=>setMealForm(f=>({...f,num_staff:e.target.value}))}/>
              </div>
              <div>
                <label style={lbl}>Cost per Head (KES)</label>
                <input style={inp} type="number" min="0" step="0.01" placeholder="e.g. 150"
                  value={mealForm.cost_per_head}
                  onChange={e=>setMealForm(f=>({...f,cost_per_head:e.target.value}))}/>
              </div>
            </div>

            {/* Live total preview */}
            {mealFormTotal > 0 && (
              <div style={{ marginBottom:12, padding:'10px 14px', background:'rgba(200,169,110,0.07)', border:'1px solid rgba(200,169,110,0.2)', borderRadius:6 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'#888' }}>Total Meal Cost</span>
                  <span style={{ fontSize:18, fontWeight:700, color:'#c8a96e' }}>{fmtK(mealFormTotal)}</span>
                </div>
                <div style={{ fontSize:10, color:'#555', marginTop:2 }}>
                  {mealForm.num_staff || 0} staff × KES {parseFloat(mealForm.cost_per_head||0).toFixed(2)}/head
                </div>
              </div>
            )}

            <div style={{ marginBottom:16 }}>
              <label style={lbl}>Notes (optional)</label>
              <textarea style={{ ...inp, height:55, resize:'vertical' }}
                placeholder="Any notes about this meal..."
                value={mealForm.notes}
                onChange={e=>setMealForm(f=>({...f,notes:e.target.value}))}/>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setMealModal(false)} style={{ flex:1, padding:9, background:'transparent', border:'1px solid #2a2a2a', borderRadius:4, color:'#888', cursor:'pointer', fontFamily:'inherit' }}>
                Cancel
              </button>
              <button onClick={saveMeal} disabled={savingMeal} style={{ flex:2, padding:9, background:'#c8a96e', border:'none', borderRadius:4, color:'#000', fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity:savingMeal?0.6:1 }}>
                {savingMeal ? 'Saving…' : editMeal ? '💾 Update Entry' : '✓ Log Meal Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function ChefPage({ notify }) {
  const [tab, setTab]              = useState('dashboard');
  const [dashboard, setDashboard]  = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes]      = useState([]);
  const [ingSearch, setIngSearch]  = useState('');
  const [recSearch, setRecSearch]  = useState('');
  const [recCat, setRecCat]        = useState('');
  const [ingModal, setIngModal]    = useState(false);
  const [editIng, setEditIng]      = useState(null);
  const [ingForm, setIngForm]      = useState({ name:'',purchase_unit:'kg',purchase_price:'',yield_pct:'100',category:'',supplier:'',stock_qty:'0',reorder_level:'0' });
  const [recModal, setRecModal]    = useState(false);
  const [editRec, setEditRec]      = useState(null);
  const [recForm, setRecForm]      = useState({ name:'',category:'main',menu_type:'food',description:'',portions:'1',target_cost_pct:'30',selling_price:'',is_active:true,publish_to_menu:true });
  const [recIngLines, setRecIngLines] = useState([]);
  const [scaleModal, setScaleModal]   = useState(null);
  const [scalePortions, setScalePortions] = useState(1);
  const [scaleResult, setScaleResult]     = useState(null);
  const [histModal, setHistModal]  = useState(null);

  const loadAll = useCallback(async () => {
    const [d,i,r] = await Promise.all([api.getChefDashboard(),api.getIngredients('?active=true'),api.getRecipes('?status=active')]);
    setDashboard(d.data); setIngredients(i.data.results||i.data); setRecipes(r.data.results||r.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const openIngModal = (ing=null) => {
    if (ing) { setEditIng(ing); setIngForm({ name:ing.name,purchase_unit:ing.purchase_unit,purchase_price:ing.purchase_price,yield_pct:ing.yield_pct,category:ing.category||'',supplier:ing.supplier||'',stock_qty:ing.stock_qty||'0',reorder_level:ing.reorder_level||'0' }); }
    else { setEditIng(null); setIngForm({ name:'',purchase_unit:'kg',purchase_price:'',yield_pct:'100',category:'',supplier:'',stock_qty:'0',reorder_level:'0' }); }
    setIngModal(true);
  };

  const saveIngredient = async () => {
    if (!ingForm.name||!ingForm.purchase_price) { notify('Name and price required','error'); return; }
    const yld=parseFloat(ingForm.yield_pct);
    if (yld<=0||yld>100) { notify('Yield must be 1–100%','error'); return; }
    const payload={ ...ingForm,purchase_price:parseFloat(ingForm.purchase_price),yield_pct:yld };
    try {
      if (editIng) await api.updateIngredient(editIng.id,payload);
      else await api.createIngredient(payload);
      setIngModal(false); await loadAll();
      notify(editIng?'Ingredient updated — all recipes recalculated!':'Ingredient added!');
    } catch(e) { notify(e.response?.data?.name?.[0]||'Error saving','error'); }
  };

  const showHistory = async (ing) => { const r=await api.getIngredientHistory(ing.id); setHistModal({ ingredient:ing,history:r.data }); };
  const epCostPreview = ingForm.purchase_price&&ingForm.yield_pct ? (parseFloat(ingForm.purchase_price)/(parseFloat(ingForm.yield_pct)/100)).toFixed(2) : null;

  const openRecModal = (rec=null) => {
    if (rec) {
      setEditRec(rec);
      setRecForm({ name:rec.name,category:rec.category,menu_type:rec.menu_type||'food',description:rec.description||'',portions:rec.portions,target_cost_pct:rec.target_cost_pct,selling_price:rec.selling_price||'',is_active:rec.is_active,publish_to_menu:true });
      setRecIngLines((rec.recipe_ingredients||[]).map(ri=>({ ingredient_id:ri.ingredient,ingredient_name:ri.ingredient_name,quantity:ri.quantity,unit:ri.unit,ep_cost:ri.ep_cost,line_cost:ri.line_cost,notes:ri.notes||'' })));
    } else { setEditRec(null); setRecForm({ name:'',category:'main',menu_type:'food',description:'',portions:'1',target_cost_pct:'30',selling_price:'',is_active:true,publish_to_menu:true }); setRecIngLines([]); }
    setRecModal(true);
  };

  const addIngLine = () => {
    if (!ingredients.length) { notify('Add ingredients first','error'); return; }
    const f=ingredients[0];
    setRecIngLines(l=>[...l,{ ingredient_id:f.id,ingredient_name:f.name,quantity:'',unit:f.purchase_unit,ep_cost:f.ep_cost,line_cost:0,notes:'' }]);
  };

  const updateIngLine = (idx,field,value) => {
    setRecIngLines(lines=>lines.map((l,i)=>{
      if (i!==idx) return l;
      let u={ ...l,[field]:value };
      if (field==='ingredient_id') { const ing=ingredients.find(x=>x.id===parseInt(value)); if (ing){ u.ingredient_name=ing.name; u.ep_cost=ing.ep_cost; u.unit=ing.purchase_unit; } }
      if (field==='quantity'||field==='ingredient_id') { u.line_cost=Math.round((parseFloat(u.quantity)||0)*parseFloat(u.ep_cost||0)*100)/100; }
      return u;
    }));
  };
  const removeIngLine = (idx) => setRecIngLines(l=>l.filter((_,i)=>i!==idx));

  const recLineTotalCost  = recIngLines.reduce((s,l)=>s+(parseFloat(l.line_cost)||0),0);
  const recPortions       = parseFloat(recForm.portions)||1;
  const recCostPerPortion = Math.round(recLineTotalCost/recPortions*100)/100;
  const recTargetPct      = parseFloat(recForm.target_cost_pct)||30;
  const recSuggestedPrice = recTargetPct>0 ? Math.round(recCostPerPortion/(recTargetPct/100)*100)/100 : 0;
  const recSellingPrice   = parseFloat(recForm.selling_price)||0;
  const recActualCostPct  = recSellingPrice>0 ? Math.round(recCostPerPortion/recSellingPrice*10000)/100 : 0;
  const recStatus         = recSellingPrice===0?'grey':recActualCostPct<=recTargetPct?'green':recActualCostPct<=recTargetPct+5?'yellow':'red';

  const saveRecipe = async () => {
    if (!recForm.name) { notify('Recipe name required','error'); return; }
    if (!recIngLines.length) { notify('Add at least one ingredient','error'); return; }
    for (const l of recIngLines) { if (!l.quantity||parseFloat(l.quantity)<=0) { notify('All ingredient quantities must be positive','error'); return; } }
    try {
      let recipeId=editRec?.id;
      const payload={ ...recForm,portions:parseInt(recForm.portions)||1,target_cost_pct:parseFloat(recForm.target_cost_pct)||30,selling_price:parseFloat(recForm.selling_price)||0 };
      if (editRec) await api.updateRecipe(recipeId,payload);
      else { const r=await api.createRecipe(payload); recipeId=r.data.id; }
      await api.setRecipeIngredients(recipeId,{ ingredients:recIngLines.map(l=>({ ingredient:l.ingredient_id,quantity:parseFloat(l.quantity),unit:l.unit,notes:l.notes })) });
      let menuMsg='';
      if (recForm.publish_to_menu&&parseFloat(recForm.selling_price)>0) {
        try { const pub=await api.publishRecipeToMenu(recipeId,{ selling_price:parseFloat(recForm.selling_price),menu_type:recForm.menu_type||'food' }); menuMsg=pub.data.status==='published'?' & published to waiter menu ✓':' & menu item updated ✓'; }
        catch(pubErr) { notify('Recipe saved but menu publish failed: '+(pubErr.response?.data?.error||'unknown error'),'warning'); }
      }
      setRecModal(false); await loadAll();
      notify((editRec?'Recipe updated':'Recipe created')+menuMsg+'!');
    } catch(e) { notify(e.response?.data?.error||'Error saving recipe','error'); }
  };

  const doScale = async () => {
    try { const r=await api.scaleRecipe(scaleModal.id,{ portions:scalePortions }); setScaleResult(r.data); }
    catch { notify('Scale error','error'); }
  };

  const exportRecipePdf = async (rec) => {
    notify('Preparing recipe PDF…');
    try {
      const url = api.exportRecipePdf(rec.id);
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file returned');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_Recipe_${rec.name.replace(/\s+/g,'_')}_CostingCard.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify('Recipe PDF downloaded ✓');
    } catch(e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
  };

  const filteredIngredients = ingredients.filter(i=>!ingSearch||i.name.toLowerCase().includes(ingSearch.toLowerCase()));
  const filteredRecipes = recipes.filter(r=>(!recSearch||r.name.toLowerCase().includes(recSearch.toLowerCase()))&&(!recCat||r.category===recCat));

  return (
    <div>
      <PageHeader icon="👨‍🍳" title="Chef — Food Costing Module" sub="Ingredient master · Recipe costing · Menu engineering · Price calculator · Budget & Staff Meals" />

      <TabBar tabs={[
        { id:'dashboard',  label:'Dashboard',        icon:'📊' },
        { id:'ingredients',label:'Ingredients',      icon:'🥩' },
        { id:'recipes',    label:'Recipes',          icon:'📋' },
        { id:'menu_eng',   label:'Menu Engineering', icon:'🗺️' },
        { id:'calculator', label:'Price Calculator', icon:'🧮' },
        { id:'spoilage',   label:'Spoilage',         icon:'🗑️' },
        { id:'budget',     label:'Budget & Meals',   icon:'💰' },
      ]} active={tab} onChange={setTab} />

      {/* DASHBOARD */}
      {tab==='dashboard' && dashboard && (
        <>
          <StatCards stats={[
            { label:'Total Recipes',   value:dashboard.total_recipes,           color:'#c8a96e', icon:'📋' },
            { label:'Avg Food Cost %', value:`${dashboard.avg_food_cost_pct}%`, color:dashboard.avg_food_cost_pct<=30?'#2ecc71':'#e74c3c', icon:'📊', sub: dashboard.avg_food_cost_pct<=30 ? 'Healthy margin' : 'Review costs' },
            { label:'Low Stock Alerts',value:dashboard.low_stock_count,         color:'#e74c3c', icon:'⚠️' },
            { label:'Ingredients',     value:ingredients.length,                color:'#3498db', icon:'🥩' },
          ]} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <DashCard title="⭐ Top Recipes by Profit">
              <RankBars
                items={(dashboard.top_recipes||[]).map(r=>({ label:r.name, value:parseFloat(r.profit_per_portion||0), color:'#2ecc71' }))}
                formatVal={v=>'KES '+v.toFixed(0)}
              />
              {!(dashboard.top_recipes?.length) && <Empty text="No recipes yet" />}
            </DashCard>
            <DashCard title="🥩 Cost Distribution">
              {dashboard.most_expensive_ingredient && (
                <div style={{ padding:'10px 12px', background:'var(--bgInput)', borderRadius:8, marginBottom:12 }}>
                  <div style={{ fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:4 }}>Priciest Ingredient</div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#e74c3c' }}>{dashboard.most_expensive_ingredient.name}</div>
                  <div style={{ fontSize:11, color:'var(--textMuted)' }}>KES {dashboard.most_expensive_ingredient.purchase_price}/{dashboard.most_expensive_ingredient.purchase_unit}</div>
                </div>
              )}
              <DonutRing
                size={100} stroke={10}
                centerLabel={dashboard.avg_food_cost_pct+'%'}
                centerSub="avg cost"
                segments={[
                  { label:'Food Cost', value: dashboard.avg_food_cost_pct, color:'#e74c3c' },
                  { label:'Margin', value: Math.max(0, 100-dashboard.avg_food_cost_pct), color:'#2ecc71' },
                ]}
              />
            </DashCard>
          </div>
          <Card title="Recipe Cost Overview">
            {(dashboard.recipes||[]).length===0 ? <Empty text="No recipes yet" /> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                  <thead><tr>{['Recipe','Category','Cost/Portion','Selling Price','Food Cost %','Target %','Profit/Portion','Status','Menu Class'].map(h=>(
                    <th key={h} style={{ background:'#0e0e0e',padding:'7px 8px',textAlign:'left',fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {dashboard.recipes.map(r=>{
                      const sm=STATUS_META[r.cost_status]||STATUS_META.grey, me=MENU_ENG[r.menu_category]||{};
                      return (
                        <tr key={r.id} style={{ borderBottom:'1px solid #1a1a1a' }}>
                          <td style={{ padding:'8px',fontWeight:600,color:'var(--textPrimary)' }}>{r.name}</td>
                          <td style={{ padding:'8px',color:'var(--textMuted)',textTransform:'capitalize' }}>{r.category}</td>
                          <td style={{ padding:'8px',fontWeight:700,color:'#c8a96e' }}>{fmtKES(r.cost_per_portion)}</td>
                          <td style={{ padding:'8px',color:'var(--textPrimary)' }}>{fmtKES(r.selling_price)}</td>
                          <td style={{ padding:'8px',fontWeight:700,color:sm.color }}>{r.actual_cost_pct}%</td>
                          <td style={{ padding:'8px',color:'var(--textFaint)' }}>{r.target_cost_pct}%</td>
                          <td style={{ padding:'8px',fontWeight:700,color:r.profit_per_portion>=0?'#2ecc71':'#e74c3c' }}>{fmtKES(r.profit_per_portion)}</td>
                          <td style={{ padding:'8px' }}><span style={{ padding:'2px 8px',borderRadius:3,fontSize:10,fontWeight:700,background:sm.bg,color:sm.color }}>{r.cost_status.toUpperCase()}</span></td>
                          <td style={{ padding:'8px' }}><span style={{ fontSize:13 }}>{me.icon}</span> <span style={{ fontSize:11,color:me.color }}>{r.menu_category}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* INGREDIENTS */}
      {tab==='ingredients' && (
        <>
          <Card action={<button style={{ ...btnSm,background:'#c8a96e',color:'#111',padding:'7px 14px',fontWeight:700 }} onClick={()=>openIngModal()}>+ Add Ingredient</button>}>
            <input style={{ ...inputStyle,marginBottom:0 }} placeholder="🔍 Search ingredients…" value={ingSearch} onChange={e=>setIngSearch(e.target.value)} />
          </Card>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10 }}>
            {filteredIngredients.length===0 ? (
              <div style={{ gridColumn:'1/-1' }}><Empty text="No ingredients yet — add your first ingredient" /></div>
            ) : filteredIngredients.map(ing=>(
              <div key={ing.id} style={{ background:'var(--bgCard)',border:'1px solid #1e1e1e',borderRadius:4,padding:14 }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:14,fontWeight:700,color:'var(--textPrimary)' }}>{ing.name}</div>
                    {ing.category && <div style={{ fontSize:11,color:'var(--textFaint)',marginTop:2 }}>{ing.category}</div>}
                  </div>
                  <div style={{ display:'flex',gap:4 }}>
                    <button style={{ ...btnSm,fontSize:11 }} onClick={()=>openIngModal(ing)}>Edit</button>
                    <button style={{ ...btnSm,fontSize:11,color:'var(--textMuted)' }} onClick={()=>showHistory(ing)}>📈</button>
                  </div>
                </div>
                <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:8,marginBottom:8 }}>
                  <div style={{ background:'var(--bgCard)',borderRadius:3,padding:'8px 10px' }}>
                    <div style={{ fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>Purchase Price</div>
                    <div style={{ fontSize:15,fontWeight:700,color:'#c8a96e' }}>KES {parseFloat(ing.purchase_price).toLocaleString('en-KE')}</div>
                    <div style={{ fontSize:10,color:'var(--textFaint)' }}>per {ing.purchase_unit}</div>
                  </div>
                  <div style={{ background:'#0a1a0a',borderRadius:3,padding:'8px 10px',border:'1px solid #2ecc7122' }}>
                    <div style={{ fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>EP Cost</div>
                    <div style={{ fontSize:15,fontWeight:700,color:'#2ecc71' }}>KES {ing.ep_cost?.toLocaleString('en-KE')}</div>
                    <div style={{ fontSize:10,color:'var(--textFaint)' }}>Yield: {ing.yield_pct}%</div>
                  </div>
                </div>
                <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--textFaint)',borderTop:'1px solid #1a1a1a',paddingTop:8 }}>
                  <span>Stock: <strong style={{ color:parseFloat(ing.stock_qty)<=parseFloat(ing.reorder_level)?'#e74c3c':'#aaa' }}>{ing.stock_qty} {ing.purchase_unit}</strong></span>
                  <span>Reorder: {ing.reorder_level} {ing.purchase_unit}</span>
                  <span>Used in: {ing.recipe_count} recipes</span>
                </div>
                {parseFloat(ing.stock_qty)<=parseFloat(ing.reorder_level) && (
                  <div style={{ marginTop:6,padding:'4px 8px',background:'#1a0a0a',borderRadius:3,fontSize:10,color:'#e74c3c',fontWeight:700 }}>⚠ LOW STOCK — Reorder needed</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* RECIPES */}
      {tab==='recipes' && (
        <>
          <Card action={<button style={{ ...btnSm,background:'#c8a96e',color:'#111',padding:'7px 14px',fontWeight:700 }} onClick={()=>openRecModal()}>+ New Recipe</button>}>
            <div style={{ display:'flex',gap:10,flexWrap:'wrap' }}>
              <input style={{ ...inputStyle,flex:1,minWidth:200 }} placeholder="🔍 Search recipes…" value={recSearch} onChange={e=>setRecSearch(e.target.value)} />
              <select style={{ ...inputStyle,width:180 }} value={recCat} onChange={e=>setRecCat(e.target.value)}>
                <option value="">All Categories</option>
                {CATEGORIES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
              </select>
            </div>
          </Card>
          <div style={{ display:'grid',gap:10 }}>
            {filteredRecipes.length===0 ? <Empty text="No recipes yet" /> : filteredRecipes.map(rec=>{
              const sm=STATUS_META[rec.cost_status]||STATUS_META.grey, me=MENU_ENG[rec.menu_category]||{};
              return (
                <div key={rec.id} style={{ background:'var(--bgCard)',border:`1px solid ${sm.color}22`,borderLeft:`4px solid ${sm.color}`,borderRadius:4,padding:14 }}>
                  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8 }}>
                    <div>
                      <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
                        <span style={{ fontSize:15,fontWeight:700,color:'var(--textPrimary)' }}>{rec.name}</span>
                        <span style={{ fontSize:11,color:'var(--textMuted)',textTransform:'capitalize' }}>{rec.category}</span>
                        <span title={me.desc} style={{ fontSize:12,color:me.color }}>{me.icon} {rec.menu_category}</span>
                      </div>
                      {rec.description && <div style={{ fontSize:12,color:'var(--textFaint)',marginTop:3 }}>{rec.description}</div>}
                    </div>
                    <div style={{ display:'flex',gap:6,flexWrap:'wrap',alignItems:'center' }}>
                      {rec.menu_item ? <span style={{ fontSize:10,padding:'3px 8px',borderRadius:10,background:'#0a1a0a',border:'1px solid #2ecc7155',color:'#2ecc71' }}>✓ On Menu</span>
                                     : <span style={{ fontSize:10,padding:'3px 8px',borderRadius:10,background:'#1a0a0a',border:'1px solid #e74c3c44',color:'var(--textMuted)' }}>Not on Menu</span>}
                      <button style={{ ...btnSm,fontSize:11 }} onClick={()=>openRecModal(rec)}>Edit</button>
                      <button className="btn btn-default btn-sm" onClick={()=>{ setScaleModal(rec); setScalePortions(rec.portions); setScaleResult(null); }}>⚖️ Scale</button>
                      <button style={{ ...btnSm,fontSize:11,color:'#e74c3c',background:'#1a0a0a' }} onClick={()=>exportRecipePdf(rec)}>📄 PDF</button>
                      {rec.selling_price>0 && (
                        <button style={{ ...btnSm,fontSize:11,color:'#2ecc71',background:'#0a1a0a',border:'1px solid #2ecc7133' }}
                          onClick={async()=>{ try { const pub=await api.publishRecipeToMenu(rec.id,{ selling_price:rec.selling_price,menu_type:rec.menu_type||'food' }); await loadAll(); notify(pub.data.status==='published'?`${rec.name} published ✓`:`${rec.name} updated ✓`); } catch(e){ notify('Publish failed','error'); } }}>
                          📋 {rec.menu_item?'Update Menu':'Publish to Menu'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8,marginTop:12 }}>
                    {[
                      { label:'Cost/Portion',   value:fmtKES(rec.cost_per_portion),   color:'#c8a96e' },
                      { label:'Selling Price',  value:fmtKES(rec.selling_price),      color:'var(--textPrimary)' },
                      { label:'Suggested',      value:fmtKES(rec.suggested_price),    color:'#3498db' },
                      { label:'Food Cost %',    value:`${rec.actual_cost_pct}%`,       color:sm.color },
                      { label:'Profit/Portion', value:fmtKES(rec.profit_per_portion),  color:rec.profit_per_portion>=0?'#2ecc71':'#e74c3c' },
                      { label:'Portions',       value:rec.portions,                   color:'var(--textMuted)' },
                    ].map(m=>(
                      <div key={m.label} style={{ background:'var(--bgCard)',borderRadius:3,padding:'8px 10px' }}>
                        <div style={{ fontSize:9,color:'#444',letterSpacing:1,textTransform:'uppercase' }}>{m.label}</div>
                        <div style={{ fontSize:14,fontWeight:700,color:m.color,marginTop:3 }}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:10,padding:'7px 10px',background:sm.bg,borderRadius:3,fontSize:12,color:sm.color,fontWeight:600 }}>
                    {sm.label}{rec.actual_cost_pct>0&&` — Target: ${rec.target_cost_pct}% · Actual: ${rec.actual_cost_pct}%`}
                  </div>
                  {rec.recipe_ingredients?.length>0 && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:6 }}>Ingredients</div>
                      <div style={{ display:'grid',gap:4 }}>
                        {rec.recipe_ingredients.map(ri=>(
                          <div key={ri.id} style={{ display:'flex',justifyContent:'space-between',fontSize:12,padding:'4px 8px',background:'var(--bgCard)',borderRadius:2 }}>
                            <span style={{ color:'var(--textMuted)' }}>{ri.ingredient_name}</span>
                            <span style={{ color:'var(--textFaint)' }}>{ri.quantity} {ri.unit}</span>
                            <span style={{ color:'var(--textMuted)' }}>EP: KES {ri.ep_cost}</span>
                            <span style={{ color:'#c8a96e',fontWeight:700 }}>KES {ri.line_cost}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* MENU ENGINEERING */}
      {tab==='menu_eng' && dashboard && (
        <>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12,marginBottom:12 }}>
            {Object.entries(MENU_ENG).map(([type,meta])=>{
              const recs=(dashboard.recipes||[]).filter(r=>r.menu_category===type);
              return (
                <Card key={type}>
                  <div style={{ textAlign:'center',marginBottom:12 }}>
                    <div style={{ fontSize:32 }}>{meta.icon}</div>
                    <div style={{ fontSize:16,fontWeight:700,color:meta.color }}>{type}</div>
                    <div style={{ fontSize:11,color:'var(--textFaint)',marginTop:2 }}>{meta.desc}</div>
                    <div style={{ fontSize:28,fontWeight:700,color:meta.color,marginTop:8 }}>{recs.length}</div>
                    <div style={{ fontSize:10,color:'#444' }}>recipes</div>
                  </div>
                  {recs.map(r=>(
                    <div key={r.id} style={{ padding:'6px 0',borderBottom:'1px solid #1a1a1a',display:'flex',justifyContent:'space-between',fontSize:12 }}>
                      <span style={{ color:'var(--textMuted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:120 }}>{r.name}</span>
                      <span style={{ color:meta.color,fontWeight:700 }}>{fmtKES(r.profit_per_portion)}/pl</span>
                    </div>
                  ))}
                  {recs.length===0 && <div style={{ textAlign:'center',color:'#333',fontSize:12,padding:'8px 0' }}>None yet</div>}
                </Card>
              );
            })}
          </div>
          <Card title="Menu Engineering Strategy Guide">
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12 }}>
              {[
                { type:'Star',       icon:'⭐',color:'#c8a96e',strategy:'PROMOTE aggressively. Feature on menu front, train staff to upsell. High profitability + popularity = your best asset.' },
                { type:'Plow Horse', icon:'🐎',color:'#3498db',strategy:'REPOSITION or reprice. Guests love these but margins are tight. Consider slight price increase or portion reduction.' },
                { type:'Puzzle',     icon:'🧩',color:'#9b59b6',strategy:'POPULARISE. High margin but low volume. Improve presentation, train staff, feature in promotions to drive sales.' },
                { type:'Dog',        icon:'🐕',color:'#e74c3c',strategy:'REVIEW or REMOVE. Low margin and low popularity. Consider replacing with a Star or reformulating the dish entirely.' },
              ].map(g=>(
                <div key={g.type} style={{ padding:'12px 14px',background:'var(--bgCard)',borderRadius:4,borderLeft:`3px solid ${g.color}` }}>
                  <div style={{ fontSize:16,marginBottom:4 }}>{g.icon} <span style={{ fontWeight:700,color:g.color }}>{g.type}</span></div>
                  <div style={{ fontSize:12,color:'var(--textFaint)',lineHeight:1.5 }}>{g.strategy}</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* PRICE CALCULATOR */}
      {tab==='calculator' && <PriceCalculator notify={notify} />}

      {/* INGREDIENT MODAL */}
      {ingModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
          <div style={{ background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:6,padding:24,maxWidth:520,width:'100%', overflowX:'hidden',maxHeight:'90vh',overflowY:'auto' }}>
            <div style={{ fontSize:15,fontWeight:700,color:'#c8a96e',marginBottom:16 }}>{editIng?'Edit Ingredient':'Add New Ingredient'}</div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Ingredient Name *"><input style={inputStyle} value={ingForm.name} onChange={e=>setIngForm(f=>({...f,name:e.target.value}))}/></Field>
              </div>
              <Field label="Purchase Unit">
                <select style={inputStyle} value={ingForm.purchase_unit} onChange={e=>setIngForm(f=>({...f,purchase_unit:e.target.value}))}>
                  {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="Purchase Price (KES) *">
                <input type="number" min="0" step="0.01" style={inputStyle} value={ingForm.purchase_price} onChange={e=>setIngForm(f=>({...f,purchase_price:e.target.value}))}/>
              </Field>
              <Field label="Yield % (1–100)">
                <input type="number" min="1" max="100" step="0.1" style={inputStyle} value={ingForm.yield_pct} onChange={e=>setIngForm(f=>({...f,yield_pct:e.target.value}))}/>
              </Field>
              <Field label="Category">
                <select style={inputStyle} value={ingForm.category} onChange={e=>setIngForm(f=>({...f,category:e.target.value}))}>
                  <option value="">— Select —</option>
                  {ING_CATS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Supplier"><input style={inputStyle} value={ingForm.supplier} onChange={e=>setIngForm(f=>({...f,supplier:e.target.value}))}/></Field>
              <Field label="Stock Qty"><input type="number" min="0" step="0.001" style={inputStyle} value={ingForm.stock_qty} onChange={e=>setIngForm(f=>({...f,stock_qty:e.target.value}))}/></Field>
              <Field label="Reorder Level"><input type="number" min="0" step="0.001" style={inputStyle} value={ingForm.reorder_level} onChange={e=>setIngForm(f=>({...f,reorder_level:e.target.value}))}/></Field>
            </div>
            {epCostPreview && (
              <div style={{ margin:'12px 0',padding:'12px 14px',background:'#0a1a0a',border:'1px solid #2ecc7133',borderRadius:4 }}>
                <div style={{ fontSize:11,color:'#2ecc71',letterSpacing:1,textTransform:'uppercase',marginBottom:4 }}>EP Cost Preview</div>
                <div style={{ fontSize:20,fontWeight:700,color:'#2ecc71' }}>KES {epCostPreview}</div>
                <div style={{ fontSize:11,color:'var(--textFaint)',marginTop:3 }}>= KES {parseFloat(ingForm.purchase_price||0).toFixed(2)} ÷ {parseFloat(ingForm.yield_pct||100)/100} (yield)</div>
              </div>
            )}
            <div style={{ display:'flex',gap:8,marginTop:14 }}>
              <button style={{ ...btnPrimary,marginTop:0,flex:1 }} onClick={saveIngredient}>{editIng?'Update Ingredient':'Save Ingredient'}</button>
              <button style={{ ...btnSm,padding:'11px 20px' }} onClick={()=>setIngModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* RECIPE MODAL */}
      {recModal && (
        <div style={{ position:'fixed',inset:0,background:'var(--bgModal)',zIndex:200,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:16,overflowY:'auto' }}>
          <div style={{ background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:6,padding:24,maxWidth:760,width:'100%', overflowX:'hidden',margin:'8px auto' }}>
            <div style={{ fontSize:15,fontWeight:700,color:'#c8a96e',marginBottom:16 }}>{editRec?'Edit Recipe':'Create New Recipe'}</div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10,marginBottom:14 }}>
              <div style={{ gridColumn:'1/-1' }}><Field label="Dish Name *"><input style={inputStyle} value={recForm.name} onChange={e=>setRecForm(f=>({...f,name:e.target.value}))}/></Field></div>
              <Field label="Category"><select style={inputStyle} value={recForm.category} onChange={e=>setRecForm(f=>({...f,category:e.target.value}))}>{CATEGORIES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}</select></Field>
              <Field label="Menu Section"><select style={inputStyle} value={recForm.menu_type} onChange={e=>setRecForm(f=>({...f,menu_type:e.target.value}))}><option value="food">🍽 Food Menu</option><option value="bar">🍺 Bar Menu</option></select></Field>
              <Field label="Portions (yield)"><input type="number" min="1" style={inputStyle} value={recForm.portions} onChange={e=>setRecForm(f=>({...f,portions:e.target.value}))}/></Field>
              <Field label="Target Food Cost %"><input type="number" min="1" max="100" style={inputStyle} value={recForm.target_cost_pct} onChange={e=>setRecForm(f=>({...f,target_cost_pct:e.target.value}))}/></Field>
              <div style={{ gridColumn:'1/-1' }}><Field label="Description"><input style={inputStyle} value={recForm.description} onChange={e=>setRecForm(f=>({...f,description:e.target.value}))}/></Field></div>
            </div>
            <div style={{ marginBottom:14 }}>
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8 }}>
                <div style={{ fontSize:11,color:'#c8a96e',letterSpacing:2,textTransform:'uppercase' }}>Ingredients</div>
                <button className="btn btn-default btn-sm" onClick={addIngLine}>+ Add Ingredient</button>
              </div>
              {recIngLines.length===0 ? (
                <div style={{ textAlign:'center',padding:'20px',color:'#444',border:'1px dashed #2a2a2a',borderRadius:4 }}>Click "+ Add Ingredient" to start building the recipe</div>
              ) : (
                <div style={{ display:'grid',gap:6 }}>
                  <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr auto', minWidth:400,gap:8,padding:'4px 8px' }}>
                    {['Ingredient','Quantity','Unit','Line Cost',''].map(h=><div key={h} style={{ fontSize:9,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>{h}</div>)}
                  </div>
                  {recIngLines.map((line,idx)=>(
                    <div key={idx} style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr auto', minWidth:400,gap:8,alignItems:'center',background:'var(--bgCard)',padding:'8px',borderRadius:3 }}>
                      <select style={{ ...inputStyle,padding:'6px 8px' }} value={line.ingredient_id} onChange={e=>updateIngLine(idx,'ingredient_id',e.target.value)}>
                        {ingredients.map(i=><option key={i.id} value={i.id}>{i.name} (EP: KES {i.ep_cost}/{i.purchase_unit})</option>)}
                      </select>
                      <input type="number" min="0.0001" step="0.0001" style={{ ...inputStyle,padding:'6px 8px' }} value={line.quantity} onChange={e=>updateIngLine(idx,'quantity',e.target.value)}/>
                      <select style={{ ...inputStyle,padding:'6px 8px' }} value={line.unit} onChange={e=>updateIngLine(idx,'unit',e.target.value)}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select>
                      <div style={{ fontWeight:700,color:'#c8a96e',fontSize:13,textAlign:'right' }}>KES {line.line_cost?.toFixed(2)||'0.00'}</div>
                      <button style={{ ...btnSm,color:'#e74c3c',background:'#1a0a0a',padding:'5px 8px' }} onClick={()=>removeIngLine(idx)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {recIngLines.length>0 && (
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10,marginBottom:14 }}>
                <div style={{ background:'var(--bgCard)',borderRadius:4,padding:'12px 14px' }}>
                  <div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>Cost Summary</div>
                  {recIngLines.map((l,i)=>(
                    <div key={i} style={{ display:'flex',justifyContent:'space-between',fontSize:12,padding:'3px 0',borderBottom:'1px solid #1a1a1a' }}>
                      <span style={{ color:'var(--textMuted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'60%' }}>{l.ingredient_name}</span>
                      <span style={{ color:'#c8a96e' }}>KES {(l.line_cost||0).toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display:'flex',justifyContent:'space-between',fontSize:14,fontWeight:700,padding:'6px 0',marginTop:4 }}>
                    <span style={{ color:'var(--textPrimary)' }}>Total ({recForm.portions} portions)</span><span style={{ color:'#c8a96e' }}>KES {recLineTotalCost.toFixed(2)}</span>
                  </div>
                  <div style={{ display:'flex',justifyContent:'space-between',fontSize:13,fontWeight:700,padding:'4px 0' }}>
                    <span style={{ color:'var(--textMuted)' }}>Cost Per Portion</span><span style={{ color:'#2ecc71',fontSize:16 }}>KES {recCostPerPortion.toFixed(2)}</span>
                  </div>
                </div>
                <div style={{ background:'var(--bgCard)',borderRadius:4,padding:'12px 14px' }}>
                  <div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>Pricing</div>
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:3 }}>Suggested Selling Price ({recTargetPct}% target):</div>
                    <div style={{ fontSize:18,fontWeight:700,color:'#3498db' }}>KES {recSuggestedPrice.toFixed(2)}</div>
                  </div>
                  <Field label="Actual Selling Price (KES)">
                    <input type="number" min="0" step="0.01" style={inputStyle} value={recForm.selling_price} onChange={e=>setRecForm(f=>({...f,selling_price:e.target.value}))}/>
                  </Field>
                  {recSellingPrice>0 && (
                    <div style={{ padding:'8px 10px',background:STATUS_META[recStatus].bg,borderRadius:3,marginTop:4 }}>
                      <div style={{ fontSize:12,fontWeight:700,color:STATUS_META[recStatus].color }}>Food Cost: {recActualCostPct}% — {STATUS_META[recStatus].label}</div>
                      <div style={{ fontSize:11,color:'var(--textFaint)',marginTop:2 }}>Profit per portion: KES {(recSellingPrice-recCostPerPortion).toFixed(2)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <label style={{ display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'10px 12px',background:'#0a1a0a',border:'1px solid #2ecc7133',borderRadius:4,marginBottom:10 }}>
              <input type="checkbox" style={{ accentColor:'#2ecc71',width:16,height:16 }} checked={recForm.publish_to_menu} onChange={e=>setRecForm(f=>({...f,publish_to_menu:e.target.checked}))}/>
              <div>
                <div style={{ fontSize:12,color:'#2ecc71',fontWeight:600 }}>📋 Publish to Waiter Menu</div>
                <div style={{ fontSize:11,color:'var(--textFaint)' }}>{recForm.publish_to_menu?(parseFloat(recForm.selling_price)>0?`Will appear on ${recForm.menu_type==='food'?'Food':'Bar'} menu at KES ${parseFloat(recForm.selling_price).toLocaleString('en-KE')} — waiters can sell it immediately`:'Set a selling price above to publish'):'Recipe saved for costing only — not visible to waiters'}</div>
              </div>
            </label>
            <div style={{ display:'flex',gap:8 }}>
              <button style={{ ...btnPrimary,marginTop:0,flex:1 }} onClick={saveRecipe}>{editRec?'Update Recipe':'Create Recipe'}</button>
              <button style={{ ...btnSm,padding:'11px 20px' }} onClick={()=>setRecModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* SCALE MODAL */}
      {scaleModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
          <div style={{ background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:6,padding:24,maxWidth:560,width:'100%', overflowX:'hidden' }}>
            <div style={{ fontSize:15,fontWeight:700,color:'#c8a96e',marginBottom:4 }}>⚖️ Scale Recipe</div>
            <div style={{ fontSize:13,color:'var(--textMuted)',marginBottom:16 }}>{scaleModal.name}</div>
            <Field label={`Target Portions (original: ${scaleModal.portions})`}>
              <input type="number" min="1" style={inputStyle} value={scalePortions} onChange={e=>setScalePortions(parseInt(e.target.value)||1)}/>
            </Field>
            <button style={{ ...btnPrimary,marginBottom:14 }} onClick={doScale}>Calculate Scale</button>
            {scaleResult && (
              <div style={{ background:'var(--bgCard)',borderRadius:4,padding:14 }}>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:12 }}>
                  <div><div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>Scale Factor</div><div style={{ fontSize:18,fontWeight:700,color:'#c8a96e' }}>×{scaleResult.scale_factor.toFixed(2)}</div></div>
                  <div><div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>Total Cost</div><div style={{ fontSize:18,fontWeight:700,color:'#c8a96e' }}>KES {scaleResult.total_cost.toFixed(2)}</div></div>
                  <div><div style={{ fontSize:10,color:'var(--textFaint)',letterSpacing:1,textTransform:'uppercase' }}>Cost/Portion</div><div style={{ fontSize:18,fontWeight:700,color:'#2ecc71' }}>KES {scaleResult.cost_per_portion.toFixed(2)}</div></div>
                </div>
                {scaleResult.ingredients.map((i,idx)=>(
                  <div key={idx} style={{ display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #1a1a1a',fontSize:12 }}>
                    <span style={{ color:'var(--textMuted)' }}>{i.ingredient}</span><span style={{ color:'var(--textMuted)' }}>{i.quantity} {i.unit}</span><span style={{ color:'#c8a96e' }}>KES {i.line_cost.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            <button style={{ ...btnSm,marginTop:14,width:'100%' }} onClick={()=>setScaleModal(null)}>Close</button>
          </div>
        </div>
      )}

      {/* SPOILAGE */}
      {tab==='spoilage' && <SpoilageTab notify={notify} />}

      {/* BUDGET & STAFF MEALS */}
      {tab==='budget' && <BudgetTab notify={notify} />}

      {/* PRICE HISTORY MODAL */}
      {histModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
          <div style={{ background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:6,padding:24,maxWidth:480,width:'100%', overflowX:'hidden',maxHeight:'80vh',overflowY:'auto' }}>
            <div style={{ fontSize:15,fontWeight:700,color:'#c8a96e',marginBottom:4 }}>📈 Price History</div>
            <div style={{ fontSize:13,color:'var(--textMuted)',marginBottom:16 }}>{histModal.ingredient.name}</div>
            {histModal.history.length===0 ? <Empty text="No price changes recorded yet" /> : histModal.history.map(h=>(
              <div key={h.id} style={{ padding:'10px 12px',background:'var(--bgCard)',borderRadius:4,marginBottom:8,borderLeft:`3px solid ${h.new_price>h.old_price?'#e74c3c':'#2ecc71'}` }}>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:4 }}>
                  <div>
                    <span style={{ color:'var(--textMuted)',fontSize:12 }}>KES {h.old_price}</span>
                    <span style={{ color:'var(--textFaint)',margin:'0 6px' }}>→</span>
                    <span style={{ fontWeight:700,color:h.new_price>h.old_price?'#e74c3c':'#2ecc71',fontSize:14 }}>KES {h.new_price}</span>
                    <span style={{ fontSize:11,marginLeft:8,color:h.new_price>h.old_price?'#e74c3c':'#2ecc71' }}>({h.new_price>h.old_price?'+':''}{((h.new_price-h.old_price)/h.old_price*100).toFixed(1)}%)</span>
                  </div>
                </div>
                <div style={{ fontSize:11,color:'var(--textFaint)' }}>{h.changed_by} · {new Date(h.changed_at).toLocaleString('en-KE')}</div>
                {h.notes && <div style={{ fontSize:11,color:'var(--textFaint)',marginTop:2 }}>{h.notes}</div>}
              </div>
            ))}
            <button style={{ ...btnSm,width:'100%',marginTop:10 }} onClick={()=>setHistModal(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}